import type { AccountListItem } from '../api-client';
import { createRefund } from '../api-client';
import { parseAmountInput } from '@lib/amount-parse';
import { todayLocalIsoDate } from '@lib/date-utils';
import { renderAccountPicker } from '../pages/account-picker';
import { showToast } from './toast';

export interface RefundSheetProps {
  original: {
    id: string;
    amount_original: number;
    currency_original: string;
    from_account_id: string | null;
    categoryLabel: string;
  };
  accounts: ReadonlyArray<AccountListItem>;
  defaultFundingAccountId: string | null;
  onClose(): void;
  onSuccess(): void;
}

export function openRefundSheet(root: HTMLElement, props: RefundSheetProps): void {
  let refundToId: string | null = props.original.from_account_id ?? props.defaultFundingAccountId;

  const render = () => {
    const acct = refundToId ? props.accounts.find((a) => a.id === refundToId) : null;
    const acctLabel = acct ? acct.name : 'Choose account';
    const disabled = !refundToId ? 'disabled' : '';

    root.innerHTML = `
      <div class="sheet-backdrop" id="refund-backdrop">
        <div class="sheet refund-sheet" role="dialog" aria-modal="true">
          <header class="sheet-header">
            <h3>Add refund</h3>
            <div class="sheet-card-name">${escapeHtml(props.original.categoryLabel)}</div>
          </header>
          <div class="sheet-body">
            <label class="sheet-field">
              <span class="sheet-label">Amount</span>
              <input id="refund-amount" type="text" inputmode="decimal" value="${props.original.amount_original.toFixed(2)}" />
              <small class="sheet-helper">Prefilled to the original amount. Edit down for a partial refund.</small>
            </label>
            <button class="sheet-row" id="refund-account-row">
              <span class="sheet-label">Refund to</span>
              <span class="sheet-value">${escapeHtml(acctLabel)}</span>
              <span class="sheet-chevron">›</span>
            </button>
            <label class="sheet-field">
              <span class="sheet-label">Date</span>
              <input id="refund-date" type="date" value="${todayLocalIsoDate()}" max="${todayLocalIsoDate()}" />
            </label>
            <div class="sheet-error" id="refund-error" hidden></div>
          </div>
          <footer class="sheet-footer">
            <button class="ghost" id="refund-cancel">Cancel</button>
            <button class="primary" id="refund-confirm" ${disabled}>Record refund</button>
          </footer>
        </div>
      </div>
    `;

    const close = () => { root.innerHTML = ''; props.onClose(); };

    root.querySelector<HTMLElement>('#refund-backdrop')!.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) close();
    });
    root.querySelector<HTMLButtonElement>('#refund-cancel')!.addEventListener('click', close);

    root.querySelector<HTMLButtonElement>('#refund-account-row')!.addEventListener('click', () => {
      const pickerMount = document.createElement('div');
      root.appendChild(pickerMount);
      renderAccountPicker(pickerMount, [...props.accounts], {
        onPick: (id) => { refundToId = id; pickerMount.remove(); render(); },
        onCancel: () => pickerMount.remove(),
      }, { filter: 'all', selectedId: refundToId, title: 'Refund to' });
    });

    root.querySelector<HTMLButtonElement>('#refund-confirm')!.addEventListener('click', async () => {
      const errorEl = root.querySelector<HTMLElement>('#refund-error')!;
      errorEl.hidden = true; errorEl.textContent = '';

      const parsed = parseAmountInput(root.querySelector<HTMLInputElement>('#refund-amount')!.value);
      if (!parsed.ok) {
        errorEl.textContent = describeParseError(parsed.code); errorEl.hidden = false; return;
      }
      if (!refundToId) { errorEl.textContent = 'Pick an account.'; errorEl.hidden = false; return; }
      const dateValue = root.querySelector<HTMLInputElement>('#refund-date')!.value;
      try {
        await createRefund({
          refund_of_transaction_id: props.original.id,
          from_account_id: refundToId,
          amount: parsed.amount,
          currency: parsed.currency,
          occurred_at: dateValue,
        });
        showToast('Refund recorded');
        root.innerHTML = '';
        props.onSuccess();
      } catch (e) {
        errorEl.textContent = e instanceof Error ? e.message : String(e);
        errorEl.hidden = false;
      }
    });
  };

  render();
}

function describeParseError(code: 'empty' | 'no_amount' | 'unknown_currency' | 'non_positive'): string {
  switch (code) {
    case 'empty':            return 'Enter an amount.';
    case 'no_amount':        return 'Enter a valid number.';
    case 'unknown_currency': return 'Unknown currency code. Use AED, USD, GBP, EUR, or CHF.';
    case 'non_positive':     return 'Amount must be greater than zero.';
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
